import { action, observable, computed, runInAction } from 'mobx'
import AsyncStore from 'stores/AsyncStore'
import Paginator from 'models/Paginator'
import { silentListDiff, SILENT_HIGHLIGHT_MS } from 'util/silentListMerge'

/**
 * Base store for list screens with silent background refresh + new-item highlight.
 *
 * Subclasses must:
 * - pass `itemsKey` (observable array property name, e.g. 'quotes')
 * - implement `getListCacheKey()`
 * - call `fetchList({ silent, request, onRetry })` from their list method
 * - include `...this.getSilentListAnnotations()` in makeObservable
 *
 * List item models should expose setSilentHighlight / clearSilentHighlight
 * (see QuoteEntity) so the green flash works in DynamicTable.
 */
class SilentListStore extends AsyncStore {
    listCache = new Map()
    fetchGeneration = 0
    highlightTimer = null
    lastListCacheKey = null

    constructor({ itemsKey = 'items', cacheExtraKeys = [] } = {}) {
        super()

        this.itemsKey = itemsKey
        this.cacheExtraKeys = cacheExtraKeys
    }

    // eslint-disable-next-line class-methods-use-this
    getSilentListAnnotations() {
        return {
            lastListCacheKey: observable,
            hasCurrentList: computed,
            saveListCache: action,
            restoreListCache: action,
            clearListCache: action,
            markHighlights: action,
            fetchList: action,
        }
    }

    get items() {
        return this[this.itemsKey] || []
    }

    set items(value) {
        this[this.itemsKey] = value
    }

    // eslint-disable-next-line class-methods-use-this
    getListCacheKey() {
        throw new Error('SilentListStore subclass must implement getListCacheKey()')
    }

    get hasCurrentList() {
        return this.items.length > 0 && this.lastListCacheKey === this.getListCacheKey()
    }

    buildCachePayload(overrides = null) {
        if (overrides) {
            return overrides
        }

        const payload = {
            items: this.items,
            paginator: this.paginator,
        }

        this.cacheExtraKeys.forEach((key) => {
            payload[key] = this[key]
        })

        return payload
    }

    applyCachePayload(payload) {
        this.items = payload.items
        this.paginator = payload.paginator

        this.cacheExtraKeys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(payload, key)) {
                this[key] = payload[key]
            }
        })
    }

    saveListCache(cacheKey = this.getListCacheKey(), payload = null) {
        this.listCache.set(cacheKey, this.buildCachePayload(payload))

        if (cacheKey === this.getListCacheKey()) {
            this.lastListCacheKey = cacheKey
        }
    }

    restoreListCache() {
        const cached = this.listCache.get(this.getListCacheKey())

        if (!cached) {
            return false
        }

        this.applyCachePayload(cached)
        this.lastListCacheKey = this.getListCacheKey()
        this.markHighlights([])

        return true
    }

    /**
     * Drop in-memory list data so a later silent refresh cannot show another user's rows.
     * Call on logout / session change.
     */
    clearListCache() {
        this.clearHighlightTimer()
        this.fetchGeneration += 1
        this.listCache.clear()
        this.lastListCacheKey = null
        this.items = []
        this.paginator = Paginator.empty()

        this.cacheExtraKeys.forEach((key) => {
            const current = this[key]

            if (Array.isArray(current)) {
                this[key] = []
            } else if (typeof current === 'string') {
                this[key] = ''
            } else {
                this[key] = null
            }
        })
    }

    clearHighlightTimer() {
        if (this.highlightTimer) {
            clearTimeout(this.highlightTimer)
            this.highlightTimer = null
        }
    }

    // eslint-disable-next-line class-methods-use-this
    applyItemHighlight(item, isNewlyAdded) {
        if (typeof item.setSilentHighlight === 'function') {
            item.setSilentHighlight({
                isNewlyAdded,
                isSilentlyUpdated: false,
            })
            return
        }

        if (typeof item.clearSilentHighlight === 'function' && !isNewlyAdded) {
            item.clearSilentHighlight()
        }
    }

    // eslint-disable-next-line class-methods-use-this
    clearItemHighlight(item) {
        if (typeof item.clearSilentHighlight === 'function') {
            item.clearSilentHighlight()
        }
    }

    markHighlights(newIds = []) {
        const newIdsSet = new Set(newIds)

        this.items.forEach((item) => {
            this.applyItemHighlight(item, newIdsSet.has(item.id))
        })

        this.clearHighlightTimer()

        if (newIds.length === 0) {
            return
        }

        this.highlightTimer = setTimeout(() => {
            runInAction(() => {
                this.items.forEach((item) => {
                    this.clearItemHighlight(item)
                })
            })
            this.highlightTimer = null
        }, SILENT_HIGHLIGHT_MS)
    }

    /**
     * @param {Object} options
     * @param {boolean} [options.silent=false]
     * @param {() => Promise<{ items: Array, paginator: Object, [key: string]: any }>} options.request
     * @param {Function} [options.onRetry] - passed to preRequest for try-again
     */
    async fetchList({ silent = false, request, onRetry } = {}) {
        if (typeof request !== 'function') {
            throw new Error('SilentListStore.fetchList requires a request function')
        }

        if (silent && this.isLoading) {
            // Persist across routes: drop stale loader state and refresh in background
            this.finishRequest()
        }

        this.fetchGeneration += 1
        const generation = this.fetchGeneration
        const requestKey = this.getListCacheKey()
        const cachedItems = this.listCache.get(requestKey)?.items
        const baselineItems = cachedItems || (silent ? this.items : [])

        if (!silent) {
            this.preRequest(onRetry)
        }

        try {
            const response = await request()
            const { items, paginator, ...extra } = response

            runInAction(() => {
                const cachePayload = {
                    items,
                    paginator,
                    ...extra,
                }

                // Always keep cache for the requested filters/tab
                this.saveListCache(requestKey, cachePayload)

                // Stale response: user already changed tab/filters
                if (generation !== this.fetchGeneration) {
                    return
                }

                this.paginator = paginator

                this.cacheExtraKeys.forEach((key) => {
                    if (Object.prototype.hasOwnProperty.call(extra, key)) {
                        this[key] = extra[key]
                    }
                })

                if (silent) {
                    const { items: nextItems, newIds } = silentListDiff(baselineItems, items)
                    this.items = nextItems
                    this.markHighlights(newIds)
                } else {
                    this.items = items
                    this.markHighlights([])
                    this.onSuccessRequest()
                }
            })
        } catch (e) {
            runInAction(() => {
                if (generation !== this.fetchGeneration) {
                    return
                }

                if (silent) {
                    return
                }

                this.onErrorRequest(e)
            })
        }
    }
}

export default SilentListStore
